import {
  json,
  redirect,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction } from
'@remix-run/cloudflare';
import {
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData } from
'@remix-run/react';
import { parse } from 'cookie';
import type { FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useFullscreen, useToggle } from 'react-use';

import { QueryClient, QueryClientProvider } from 'react-query';
import tailwind from '~/styles/tailwind.css';
import { elementNotContainedByClickTarget } from './utils/elementNotContainedByClickTarget';
import getUsername from './utils/getUsername.server';
import { cn } from './utils/style';

// The admin token is the master token that can always access the application
const ADMIN_TOKEN = 'DB4F4AE7-58E9-4D50-A5E0-753A44C84B17';

// Tokens can be generated dynamically, but we need to check against valid ones
// In a real application, you would store these in a database
// For simplicity, we'll use a hard-coded array here
const VALID_TOKENS = [
ADMIN_TOKEN
// Add additional valid tokens here as needed
];

// Name of the cookie that will store the authentication token
const AUTH_COOKIE_NAME = 'ek-ghanta-auth';

// Cookie expiration in days
const COOKIE_EXPIRATION_DAYS = 30;

function addOneDay(date: Date): Date {
  const result = new Date(date);
  result.setTime(result.getTime() + 24 * 60 * 60 * 1000);
  return result;
}

/**
 * Creates a cookie with the auth token
 */
function createAuthCookie(token: string): string {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + COOKIE_EXPIRATION_DAYS);

  return `${AUTH_COOKIE_NAME}=${token}; Path=/; HttpOnly; Secure; SameSite=Lax; Expires=${expirationDate.toUTCString()}`;
}

/**
 * Check if the request has a valid auth token in URL or cookies
 */
function checkAuthentication(request: Request): {
  isAuthenticated: boolean;
  token?: string;
} {
  const url = new URL(request.url);
  const urlToken = url.searchParams.get('token');

  // Check URL token first
  if (urlToken && (VALID_TOKENS.includes(urlToken) || urlToken.length >= 32)) {
    return { isAuthenticated: true, token: urlToken };
  }

  // If no valid URL token, check cookies
  const cookieHeader = request.headers.get('Cookie');
  if (cookieHeader) {
    const cookies = parse(cookieHeader);
    const cookieToken = cookies[AUTH_COOKIE_NAME];

    if (
    cookieToken && (
    VALID_TOKENS.includes(cookieToken) || cookieToken.length >= 32))
    {
      return { isAuthenticated: true, token: cookieToken };
    }
  }

  return { isAuthenticated: false };
}

/**
 * Detect the user's country from Cloudflare headers or use mock data in development
 */
function detectCountry(request: Request): {
  country: string;
  isInAustralia: boolean;
  isMockData: boolean;
} {
  const url = new URL(request.url);
  const mockLocation = url.searchParams.get('mockLocation');

  // Use mockLocation query parameter if provided
  if (mockLocation === 'australia') {
    return {
      country: 'AU',
      isInAustralia: true,
      isMockData: true
    };
  }

  // Try to get country from Cloudflare header
  const cfCountryHeader = request.headers.get('cf-ipcountry');

  if (cfCountryHeader) {
    // We have a real Cloudflare country header
    return {
      country: cfCountryHeader,
      isInAustralia: cfCountryHeader === 'AU',
      isMockData: false
    };
  }

  // Fallback to US if no header or mock
  return {
    country: 'US',
    isInAustralia: false,
    isMockData: true
  };
}

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  // Special case for the unauthorized page - should be accessible without authentication
  if (url.pathname === '/unauthorized') {
    return json({
      userDirectoryUrl: context.env.USER_DIRECTORY_URL,
      geolocation: detectCountry(request),
      isAuthenticated: false,
      isAdmin: false
    });
  }

  // Check authentication
  const { isAuthenticated, token } = checkAuthentication(request);

  if (!isAuthenticated) {
    throw redirect('/unauthorized');
  }

  // Set headers to include auth cookie if token is in URL
  const headers = new Headers();
  const urlToken = url.searchParams.get('token');

  if (urlToken && (VALID_TOKENS.includes(urlToken) || urlToken.length >= 32)) {
    headers.append('Set-Cookie', createAuthCookie(urlToken));

    // Redirect to the same URL without the token parameter
    const cleanUrl = new URL(url);
    cleanUrl.searchParams.delete('token');

    throw redirect(cleanUrl.toString(), { headers });
  }

  // Username check - now we know the user is authenticated
  // Redirect to set-username only if the current path isn't already /set-username
  const username = await getUsername(request);
  if (!username && url.pathname !== '/set-username') {
    const redirectUrl = new URL(url);
    redirectUrl.pathname = '/set-username';
    redirectUrl.searchParams.set('return-url', request.url);

    throw redirect(
      redirectUrl.toString(),
      token ?
      {
        headers: new Headers({ 'Set-Cookie': createAuthCookie(token) })
      } :
      undefined
    );
  }

  // Detect the user's country using Cloudflare headers
  const geolocation = detectCountry(request);

  const defaultResponse = json(
    {
      userDirectoryUrl: context.env.USER_DIRECTORY_URL,
      geolocation,
      isAuthenticated: true,
      isAdmin: token === ADMIN_TOKEN
    },
    // Preserve auth cookie in the response
    token ?
    { headers: new Headers({ 'Set-Cookie': createAuthCookie(token) }) } :
    undefined
  );

  // we only care about verifying token freshness if request was a user
  // initiated document request.
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Sec-Fetch-User
  const secFetchUser = request.headers.get('Sec-Fetch-User');
  if (secFetchUser !== '?1') return defaultResponse;
  const cookiesHeader = request.headers.get('Cookie');
  if (!cookiesHeader) return defaultResponse;
  const { CF_Authorization } = parse(cookiesHeader);
  if (!CF_Authorization) return defaultResponse;

  const [, payload] = CF_Authorization.split('.');
  const data = JSON.parse(atob(payload));
  const expires = new Date(data.exp * 1000);
  const now = new Date();
  if (addOneDay(now) > expires) {
    const headers = new Headers();
    ['CF_Authorization', 'CF_AppSession'].forEach((cookieName) =>
    headers.append(
      'Set-Cookie',
      `${cookieName}=; Expires=${new Date(0).toUTCString()}; Path=/;`
    )
    );

    throw redirect(request.url, { headers });
  }

  return defaultResponse;
};

export const meta: MetaFunction = () => [
{
  title: 'Ghanta'
}];


export const links: LinksFunction = () => [
{ rel: 'stylesheet', href: tailwind },
{
  rel: 'apple-touch-icon',
  sizes: '180x180',
  href: '/apple-touch-icon.png?v=ghanta-video'
},
{
  rel: 'icon',
  type: 'image/png',
  sizes: '32x32',
  href: '/favicon-32x32.png?v=ghanta-video'
},
{
  rel: 'icon',
  type: 'image/png',
  sizes: '16x16',
  href: '/favicon-16x16.png?v=ghanta-video'
},
{
  rel: 'manifest',
  href: '/site.webmanifest',
  crossOrigin: 'use-credentials'
},
{
  rel: 'mask-icon',
  href: '/safari-pinned-tab.svg?v=ghanta-video',
  color: '#8348fc'
},
{
  rel: 'shortcut icon',
  href: '/favicon.ico?v=ghanta'
}];


const Document: FC<{children?: ReactNode;}> = ({ children }) => {
  const fullscreenRef = useRef<HTMLBodyElement>(null);
  const [fullscreenEnabled, toggleFullscreen] = useToggle(false);
  useFullscreen(fullscreenRef, fullscreenEnabled, {
    onClose: () => toggleFullscreen(false)
  });
  return (
    // some extensions add data attributes to the html
    // element that React complains about.
    <html className="h-full" lang="en" suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta name="apple-mobile-web-app-title" content="Ghanta" />
				<meta name="application-name" content="Ghanta" />
				<meta name="msapplication-TileColor" content="#8348fc" />
				<meta
          name="theme-color"
          content="#f3f0ff"
          media="(prefers-color-scheme: light)" />

				<meta
          name="theme-color"
          content="#18181b"
          media="(prefers-color-scheme: dark)" />

				<Meta />
				<Links />
			</head>
			<body
        className={cn(
          'h-full',
          'bg-white',
          'text-zinc-800',
          'dark:bg-zinc-900',
          'dark:text-primary-50'
        )}
        ref={fullscreenRef}
        onDoubleClick={(e) => {
          if (
          e.target instanceof HTMLElement &&
          !elementNotContainedByClickTarget(e.target))

          toggleFullscreen();
        }}>

				{children}
				<ScrollRestoration />
				<div className="hidden" suppressHydrationWarning>
					{/* Replaced in entry.server.ts */}
					__CLIENT_ENV__
				</div>
				<Scripts />
				<LiveReload />
			</body>
		</html>);

};

export const ErrorBoundary = () => {
  return (
    <Document>
			<div className="grid h-full place-items-center">
				<p>
					It looks like there was an error, but don't worry it has been
					reported. Sorry about that!
				</p>
			</div>
		</Document>);

};

export default function App() {
  const loaderData = useLoaderData<typeof loader>();
  const [queryClient] = useState(() => new QueryClient());

  return (
    <Document>
			<QueryClientProvider client={queryClient}>
				<div className="flex flex-col h-screen bg-inherit isolate">
					{loaderData.isAdmin &&
          <div className="bg-black text-white text-xs p-1 text-center">
							Admin Mode -{' '}
							<Link to="/share-link" className="underline">
								Generate Share Links
							</Link>
						</div>}

					<div className="flex-1 overflow-hidden">
						<Outlet context={loaderData} />
					</div>
				</div>
			</QueryClientProvider>
		</Document>);

}